import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { User, UserService, UserTeam } from 'app/frontend/lib/shared/util/user';
import { DaysService } from '../../timeline/daily-overview/days.service';
import { CreatedMoods, MoodsService } from '../../timeline/timeline-item/moods/moods.service';
import { Author, Kudos, TimelineItemDialogService, Topic } from "@teammood/timeline";
import { catchError, filter, from, map, mergeMap, Subject, takeUntil } from "rxjs";
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { LoggerService } from "@teammood/shared/util/logger";
import { TranslateService } from "@ngx-translate/core";
import { UserRequestService } from "../../timeline/user-requests/user-request.service";
import { TimelineItemCreation } from "../../timeline/timeline.types";
import { UserRequestBacklog } from "../../timeline/user-requests/user-request.types";
import { UserRequestEvent } from "../../timeline/user-requests/user-requests.component";
import { ActivatedRoute, Router } from "@angular/router";
import { UrlService } from "@teammood/shared/util/url";


@Component({
    selector: 'add-other-feedback',
    templateUrl: './add-other-feedback.component.html',
    styleUrls: ['./add-other-feedback.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddOtherFeedbackComponent implements OnInit, OnDestroy {
    teamLength: number;
    teams: UserTeam[] = [];

    kudosSuccess: KudosResponse[] = [];

    wellbeingResources: WellbeingResourcesInfo = undefined;
    userRequestBacklog: UserRequestBacklog
    teamId: string;
    author: Author;

    protected pollPublishDate: number;
    protected errorMessageKey: string;

    protected landingPage: string = "/home";

    private shouldNotNotifyOnTopicCreation: boolean = false;

    private _destroy$ = new Subject<void>();

    constructor(
        private log: LoggerService, 
        private translate: TranslateService,
        private userService: UserService, 
        private moodsService: MoodsService, 
        private daysService: DaysService, 
        private snackBarService: SnackBarService,
        private timelineItemDialogService: TimelineItemDialogService,
        private userRequestService: UserRequestService,
        private route: ActivatedRoute,
        private router: Router,
        private urlService: UrlService,
    ){}

    async ngOnInit() {

        this.route.params
            .pipe(
                filter(params => !!params),
                mergeMap(params => {
                    this.pollPublishDate = params['pollPublishDate'];
                    this.errorMessageKey = params['errorMessage'];

                    return from(this.userService.get());
                }),
                filter(user => !!user),
                map(async (user: User) => {
                    this.author = {
                        id: user.id,
                        fullname: user.fullname
                    };
                    this.teams = [...user.teams];
                    this.teamId = this.teams[0].id
                    this.shouldNotNotifyOnTopicCreation = user.featureTags.find(feat => feat.id === 'NoNotificationOnTopicCreationFeature') !== undefined;

                    // check if last mood given is bad to display wellbeingResources if activated feature
                    const moodId = this.route.snapshot.params['moodId'];
                    if (moodId) {
                        const moods: CreatedMoods = await this.moodsService.getMoods(moodId);
                        const hasBadDay = moods.moods.filter(mood => {
                            return mood.mood == "bad"; //contains at least a bad mood
                        }).length > 0;

                        if (hasBadDay) {
                            const wellbeingResources = user.featureTags.find(feat => feat.id === 'WellBeingResourcesFeature$');

                            if (wellbeingResources && wellbeingResources.values['ctaLabel']) {

                                this.wellbeingResources = {
                                    title: wellbeingResources.values['title'],
                                    descriptionLine1: wellbeingResources.values['descriptionLine1'],
                                    descriptionLine2: wellbeingResources.values['descriptionLine2'],
                                    ctaUrl: wellbeingResources.values['ctaUrl'],
                                    ctaLabel: wellbeingResources.values['ctaLabel'],
                                };
                            }
                        }
                    }
                    // otherwise check user request to display question or kudos requests
                    if(this.wellbeingResources === undefined){
                        this.userRequestBacklog = await this.userRequestService.nextUserRequest();
                    }
                }),
                catchError((error) => {
                    this.log.error("Error getting the user", error);
                    return this.router.navigate(['/login']);
                }),
                takeUntil(this._destroy$),
            ).subscribe();
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    async onCreateTimelineItemAfterRequest(event: UserRequestEvent){
        if(event){
            switch(event.type){
                case 'next': 
                    await this.getNextUserRequest(event.nextRequestId);
                break;
                case 'moodItem': 
                    return; // shoult not happend 
                case 'questionItem': 
                    await this.getNextUserRequest(event.nextRequestId);
                    break;
                case 'topicItem': 
                    await this.addTopic(event.value as Topic);
                    await this.getNextUserRequest(event.nextRequestId);
                    break;
                case 'kudosItem': 
                    await this.addKudos(event.value as Kudos);
                    await this.getNextUserRequest(event.nextRequestId);
                    break;
            }
        }
    }

    async getNextUserRequest(nextRequestId = null) {
        // no teamId to set, as we want request for all belonging teams.
        this.userRequestBacklog = await this.userRequestService.nextUserRequest(null, nextRequestId);
    }

    async addKudos(kudos: Kudos){
        if (kudos.message?.body) {
            kudos.message.body = this.moodsService.addSpaceBeforeLink(kudos.message.body);
            const teamId = kudos.teamId;
            this.daysService.createKudos(kudos, teamId).then((response: any) => {
                const kudosTimelineId = response.event.item.id;
                const recipientTextList = kudos.recipients.selectedTeammates.length === 0 ? this.translate.instant('kudos.card.description.all') : kudos.recipients.selectedTeammates.map(recipient => recipient.fullname).join(', ');
                const successContent = this.translate.instant('kudos.success.description_1') + ' ' + recipientTextList + ' ' + this.translate.instant('kudos.success.description_2');
                this.kudosSuccess.unshift({
                    teamId: teamId,
                    id: kudosTimelineId,
                    content: successContent
                })
                this.snackBarService.success(this.translate.instant('kudos_created'));
            }).catch((error) => {
                this.log.error('Error creating kudos:', error);
                this.snackBarService.error(error);
            });
        }
    }

    private async addTopic(topic: Topic) {
        if (topic.title?.body) {
            topic.title.body = this.moodsService.addSpaceBeforeLink(topic.title.body);
            return this.daysService.createTopic(topic, this.teamId).then(async (response: any) => {
                const timelineItemCreation: TimelineItemCreation = response;
                const currentTopic: Topic = timelineItemCreation.event.item as Topic;
                const teamSize = timelineItemCreation.event.teamSize;

                if (!this.shouldNotNotifyOnTopicCreation) {
                    const notificationDialog = await this.timelineItemDialogService.notify(currentTopic, teamSize);

                    notificationDialog.beforeClosed().pipe(
                        filter(shouldNotify => !!shouldNotify),
                        mergeMap(async () => {
                            return this.notifyAboutTopicCreation(currentTopic);
                        }),
                        takeUntil(notificationDialog.afterClosed())
                    ).subscribe();
                }

                this.snackBarService.success(this.translate.instant('topic_created'));
            }).catch((error) => {
                this.log.error('Error creating topic:', error);
                this.snackBarService.error(error);
            });
        }
    }

    async notifyAboutTopicCreation(topic: Topic) {
        return await this.daysService.notifyAboutTopicCreation(topic.id).then(() => {
            this.snackBarService.success(this.translate.instant('topic_created_notify'));
        }).catch((error) => {
            this.log.error('Error notify about a topic:', error);
            this.snackBarService.error(error);
        });
    }

}


interface KudosResponse {
    teamId: string;
    id: string;
    content: string;
}

interface WellbeingResourcesInfo {
    title: string;
    descriptionLine1: string;
    descriptionLine2: string;
    ctaUrl: string;
    ctaLabel: string;
}